import { Button } from '@progress/kendo-react-buttons';
import { TextField } from 'app/components/form-fields';
import { useShareAccessEquipmentGuideContext } from 'app/hooks/access-sharing';
import ill_warning_small from 'assets/images/ill_warning_small.svg';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import './FleetNameTextField.less';
import clsx from 'clsx';
import { MAX_FLEET_NAME_LENGTH } from './constants';
import { lettersNumbersAndSpecialsRegex } from 'app/common/utils';
import useInputPatternValidation from 'app/hooks/form-fields/useInputPatternValidation';
import { InputChangeEvent, InputHandle } from '@progress/kendo-react-inputs';

interface Props {
  isAllEquipmentDisabled: boolean;
  selectedFleetName?: string;
  fleetNameError: string;
  className?: string;
  setSelectedFleetName: (name: string) => void;
  clearFieldError: () => void;
}

const FleetNameTextField = forwardRef<InputHandle, Props>(
  (
    {
      isAllEquipmentDisabled,
      selectedFleetName,
      fleetNameError,
      className,
      setSelectedFleetName,
      clearFieldError,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const { isPatternMatch, onKeyDown } = useInputPatternValidation(
      lettersNumbersAndSpecialsRegex,
    );

    const { openGuide } = useShareAccessEquipmentGuideContext();

    const handleChange = (e: InputChangeEvent) => {
      const inputValue = e.target.value as string;

      if (isPatternMatch(inputValue)) {
        setSelectedFleetName(inputValue);
        clearFieldError();
      }
    };

    if (isAllEquipmentDisabled) {
      return (
        <div
          className={clsx(
            'fleet-name-text-field__no-equipment-field',
            className,
          )}
        >
          <img src={ill_warning_small} alt="" />
          <span className="fleet-name-text-field__text">
            {t('all_equipment_shared')}
          </span>

          <Button
            themeColor="primary"
            className="fleet-name-text-field__learn-more-btn"
            onClick={() => openGuide()}
          >
            {t('learn_more')}
          </Button>
        </div>
      );
    }

    return (
      <TextField
        ref={ref}
        label={t('fleet_name_label')}
        required
        value={selectedFleetName}
        placeholder={t('access_sharing.dialogs.edit_fleet_name.placeholder')}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        maxLength={MAX_FLEET_NAME_LENGTH}
        error={fleetNameError}
        className={className}
      />
    );
  },
);

export default FleetNameTextField;
